import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Upload, X } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/sonner';
import { tripsAPI } from '../../lib/api';
import { messageErreur } from '../../lib/erreurs';
import { lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';
import { EXTENSIONS_LISTE, lotImportApi } from '../../lib/lotImportApi';
import { jourDuJour } from '../../lib/lotsFiltres';
import { lignesPourEnvoi, resumeGrille } from '../../lib/lotsCollage';
import { useTransporteursLots } from '../../hooks/useTransporteursLots';
import GrilleCamions from './GrilleCamions';
import { ListeRefus } from './lotsUi';

// Le code de la devise part à l’API, son libellé s’affiche.
const LIBELLES_DEVISES = { XOF: 'FCFA', EUR: 'Euro', USD: 'Dollar' };
const DEVISES = Object.entries(LIBELLES_DEVISES).map(([value, label]) => ({ value, label }));

const vide = () => ({
  societe: '',
  promis: '',
  prix: '',
  devise: 'XOF',
  date: jourDuJour(),
  depot: '',
  notes: '',
});

const depuisLot = (lot) => ({
  societe: lot?.transporter_company_id || '',
  promis: lot?.promised_trucks ? String(lot.promised_trucks) : '',
  prix: lot?.price_per_liter === null || lot?.price_per_liter === undefined ? '' : String(lot.price_per_liter),
  devise: lot?.currency || 'XOF',
  date: lot?.attributed_at ? String(lot.attributed_at).slice(0, 10) : jourDuJour(),
  depot: lot?.default_depot || '',
  notes: lot?.notes || '',
});

const FICHIER_VALIDE = /\.(xlsx|csv|pdf)$/i;

/** Phrase des lignes ignorées ou refusées, avec le message du serveur. */
const lignesRefus = (liste = []) =>
  liste.map((ligne) => ({ plaque: ligne.tractor_plate || (ligne.row ? `Ligne ${ligne.row}` : ''), message: ligne.reason }));

/**
 * Création ou modification d'un lot.
 *
 * - création (`lot` absent) : en-tête et liste des camions (collée ou saisie) ;
 *   le résultat du serveur (ajoutés, ignorés, refusés) s'affiche avant d'ouvrir le lot.
 *   Un fichier facultatif (Excel ou PDF) est déposé une fois le lot créé : l'écran du lot
 *   s'ouvre alors sur le brouillon à relire, rien n'entre dans le lot avant la confirmation ;
 * - modification (`lot` donné) : en-tête seul. Transporteur, prix et devise ne
 *   changent plus dès qu'un camion est confirmé (`camionsConfirmes`).
 *
 * Le prix au litre n'existe que pour qui voit les montants (`voitArgent`).
 */
export const LotDialog = ({
  open,
  onOpenChange,
  lot = null,
  camionsConfirmes = 0,
  voitArgent = false,
  onSaved,
}) => {
  const navigate = useNavigate();
  const modification = Boolean(lot);
  const [valeurs, setValeurs] = useState(vide);
  const [lignes, setLignes] = useState([]);
  const [depots, setDepots] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState(null);
  const [fichier, setFichier] = useState(null);
  const [suiteImport, setSuiteImport] = useState(null);
  const champFichier = useRef(null);
  const cle = useRef(null);
  const { options: societes } = useTransporteursLots();

  // À chaque ouverture : un formulaire neuf (ou celui du lot), une nouvelle clé.
  useEffect(() => {
    if (!open) return;
    setValeurs(modification ? depuisLot(lot) : vide());
    setLignes([]);
    setResultat(null);
    setFichier(null);
    setSuiteImport(null);
    setEnCours(false);
    cle.current = nouvelleCleLot('lot');
    Promise.resolve(tripsAPI.getLoadingDepots())
      .then((liste) => setDepots(Array.isArray(liste) ? liste : []))
      .catch(() => setDepots([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choisirFichier = (evenement) => {
    const choisi = evenement.target.files?.[0];
    evenement.target.value = '';
    if (!choisi) return;
    if (!FICHIER_VALIDE.test(choisi.name)) {
      toast.error('Choisissez un fichier Excel (.xlsx), CSV ou PDF.');
      return;
    }
    setFichier(choisi);
  };

  const changer = (champ, valeur) => setValeurs((avant) => ({ ...avant, [champ]: valeur }));

  const verrouille = modification && camionsConfirmes > 0;
  const resume = resumeGrille(lignes);
  const sansTransporteur = !valeurs.societe;
  const prixIllisible = voitArgent && valeurs.prix !== '' && !(Number(String(valeurs.prix).replace(',', '.')) >= 0);
  const promisIllisible = valeurs.promis !== '' && !/^[1-9]\d{0,3}$/.test(String(valeurs.promis).trim());
  const invalide = sansTransporteur || prixIllisible || promisIllisible || resume.sansPlaque > 0;

  const corps = () => {
    const base = {
      transporter_company_id: valeurs.societe || null,
      promised_trucks: valeurs.promis === '' ? null : Number(String(valeurs.promis).trim()),
      attributed_at: valeurs.date || null,
      default_depot: valeurs.depot.trim() || null,
      notes: valeurs.notes.trim() || null,
    };
    if (voitArgent && !verrouille) {
      base.currency = valeurs.devise;
      base.price_per_liter = valeurs.prix === '' ? null : Number(String(valeurs.prix).replace(',', '.'));
    }
    return base;
  };

  const enregistrer = async (evenement) => {
    evenement.preventDefault();
    if (enCours || invalide) return;
    setEnCours(true);
    try {
      if (modification) {
        const donnees = corps();
        if (verrouille) {
          delete donnees.transporter_company_id;
        }
        const detail = await lotsAPI.update(lot.id, donnees, cle.current);
        cle.current = null;
        toast.success('Lot modifié');
        onSaved?.(detail);
        onOpenChange(false);
      } else {
        const reponse = await lotsAPI.create({ ...corps(), trucks: lignesPourEnvoi(lignes) }, cle.current);
        cle.current = null;
        const refus = (reponse?.skipped || []).length + (reponse?.rejected || []).length;
        // Le lot existe : la liste jointe se dépose maintenant. Un échec ici n'annule pas le lot.
        let travail = null;
        if (fichier && reponse?.lot?.id) {
          try {
            travail = await lotImportApi.upload(reponse.lot.id, fichier);
          } catch (err) {
            toast.error(messageErreur(err, 'Le lot est créé, mais la liste n’a pas pu être déposée'));
          }
        }
        const suite = travail ? { state: { importer: true, job: travail } } : undefined;
        setSuiteImport(suite || null);
        onSaved?.(reponse);
        if (refus === 0) {
          const nombre = (reponse?.added || []).length;
          toast.success(
            nombre > 0
              ? `Lot ${reponse?.lot?.reference || ''} créé : ${nombre} camion${nombre > 1 ? 's' : ''}`.replace('  ', ' ')
              : `Lot ${reponse?.lot?.reference || ''} créé`.replace('  ', ' '),
          );
          onOpenChange(false);
          if (reponse?.lot?.id) navigate(`/lots/${reponse.lot.id}`, suite);
        } else {
          // Des plaques n'ont pas passé : on les montre avant d'ouvrir le lot.
          setResultat(reponse);
        }
      }
    } catch (err) {
      toast.error(messageErreur(err, modification ? "Le lot n'a pas pu être modifié" : "Le lot n'a pas pu être créé"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="lot-dialog">
        <DialogHeader>
          <DialogTitle>{modification ? `Modifier le lot ${lot.reference || ''}`.trim() : 'Nouveau lot'}</DialogTitle>
          <DialogDescription className="sr-only">
            {modification ? 'Modifier les informations du lot.' : 'Créer un lot et la liste de ses camions.'}
          </DialogDescription>
        </DialogHeader>

        {resultat ? (
          <div className="space-y-3" data-testid="lot-resultat-creation">
            <p className="text-sm text-foreground">
              Lot {resultat.lot?.reference} créé : {(resultat.added || []).length} camion
              {(resultat.added || []).length > 1 ? 's' : ''} ajouté{(resultat.added || []).length > 1 ? 's' : ''}.
            </p>
            <ListeRefus titre="Plaques ignorées" lignes={lignesRefus(resultat.skipped)} ton="neutre" />
            <ListeRefus titre="Lignes refusées" lignes={lignesRefus(resultat.rejected)} />
            <DialogFooter>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  if (resultat.lot?.id) navigate(`/lots/${resultat.lot.id}`, suiteImport || undefined);
                }}
                data-testid="lot-ouvrir"
              >
                Ouvrir le lot
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={enregistrer} className="min-w-0 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Transporteur *</Label>
                <SearchableSelect
                  className="input-base"
                  data-testid="lot-transporteur"
                  aria-label="Transporteur"
                  placeholder="Choisir un transporteur"
                  searchPlaceholder="Rechercher un transporteur…"
                  emptyMessage="Aucun transporteur trouvé"
                  value={valeurs.societe}
                  onValueChange={(valeur) => changer('societe', valeur)}
                  options={societes}
                  disabled={verrouille}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lot-promis">Nombre total de camions</Label>
                <Input
                  id="lot-promis"
                  inputMode="numeric"
                  value={valeurs.promis}
                  onChange={(evenement) => changer('promis', evenement.target.value)}
                  aria-invalid={promisIllisible || undefined}
                  className="input-base text-right tabular-nums"
                  data-testid="lot-promis"
                />
              </div>
              {voitArgent ? (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="lot-prix">Prix au litre</Label>
                    <Input
                      id="lot-prix"
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      value={valeurs.prix}
                      onChange={(evenement) => changer('prix', evenement.target.value)}
                      disabled={verrouille}
                      className="input-base text-right tabular-nums"
                      data-testid="lot-prix"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Devise</Label>
                    <Select
                      value={valeurs.devise}
                      onValueChange={(valeur) => changer('devise', valeur)}
                      disabled={verrouille}
                    >
                      <SelectTrigger className="input-base" aria-label="Devise" data-testid="lot-devise">
                        <SelectValue placeholder="Devise" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEVISES.map((devise) => (
                          <SelectItem key={devise.value} value={devise.value}>
                            {devise.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : null}
              <div className="space-y-1">
                <Label htmlFor="lot-date">Date d’attribution</Label>
                <Input
                  id="lot-date"
                  type="date"
                  value={valeurs.date}
                  onChange={(evenement) => changer('date', evenement.target.value)}
                  className="input-base"
                  data-testid="lot-date"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lot-depot">Dépôt par défaut</Label>
                <Input
                  id="lot-depot"
                  list="lot-depots"
                  value={valeurs.depot}
                  onChange={(evenement) => changer('depot', evenement.target.value)}
                  className="input-base uppercase"
                  data-testid="lot-depot"
                />
                <datalist id="lot-depots">
                  {depots.map((depot) => (
                    <option key={depot} value={depot} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="lot-notes">Notes</Label>
                <Textarea
                  id="lot-notes"
                  rows={2}
                  value={valeurs.notes}
                  onChange={(evenement) => changer('notes', evenement.target.value)}
                  data-testid="lot-notes"
                />
              </div>
            </div>

            {!modification ? <GrilleCamions lignes={lignes} onChange={setLignes} disabled={enCours} /> : null}

            {!modification ? (
              <div className="flex flex-wrap items-center gap-2" data-testid="lot-liste-fichier-zone">
                <input
                  ref={champFichier}
                  type="file"
                  accept={EXTENSIONS_LISTE}
                  className="sr-only"
                  onChange={choisirFichier}
                  data-testid="lot-liste-fichier"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => champFichier.current?.click()}
                  disabled={enCours}
                  data-testid="lot-liste-choisir"
                >
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  Liste des camions (Excel ou PDF)
                </Button>
                {fichier ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 pl-2.5 pr-1 text-xs">
                    <span className="max-w-[16rem] truncate" data-testid="lot-liste-nom">
                      {fichier.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFichier(null)}
                      disabled={enCours}
                      aria-label="Retirer le fichier"
                      className="rounded-full p-0.5 hover:bg-muted"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </span>
                ) : null}
              </div>
            ) : null}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
                Annuler
              </Button>
              <Button type="submit" disabled={enCours || invalide} data-testid="lot-enregistrer">
                {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {modification
                  ? 'Enregistrer'
                  : resume.total > 0
                    ? `Créer le lot et ${resume.total} camion${resume.total > 1 ? 's' : ''}`
                    : fichier
                      ? 'Créer le lot et importer la liste'
                      : 'Créer le lot'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LotDialog;
